import {
  FilesetResolver,
  FaceLandmarker,
  FaceLandmarkerResult,
} from "@mediapipe/tasks-vision";

const WASM_CDN =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task";

// Module-level cache — survives component remounts and retries within the same page session.
// First init fetches ~8MB total (WASM + model); subsequent inits reuse the cache instantly.
let _cachedVision: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>> | null = null;
let _cachedModelBuffer: ArrayBuffer | null = null;

export interface FaceDetectionResult {
  faceDetected: boolean;
  blendshapes: Map<string, number>;
  transformMatrix: number[] | null;
}

export class FaceDetectorEngine {
  private landmarker: FaceLandmarker | null = null;

  async init(): Promise<void> {
    // Reuse WASM fileset — avoids re-fetching ~3MB on retry / remount
    if (!_cachedVision) {
      _cachedVision = await FilesetResolver.forVisionTasks(WASM_CDN);
    }

    // Reuse model buffer — avoids re-fetching ~5MB on retry / remount
    if (!_cachedModelBuffer) {
      const res = await fetch(MODEL_URL);
      if (!res.ok) {
        throw new Error(`Failed to download face model (${res.status})`);
      }
      _cachedModelBuffer = await res.arrayBuffer();
    }

    this.landmarker = await FaceLandmarker.createFromOptions(_cachedVision, {
      baseOptions: {
        // Pass a copy — MediaPipe takes ownership of the buffer
        modelAssetBuffer: new Uint8Array(_cachedModelBuffer.slice(0)),
      },
      runningMode: "VIDEO",
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
      numFaces: 1,
    });
  }

  detect(video: HTMLVideoElement, timestamp: number): FaceDetectionResult {
    if (!this.landmarker) {
      return { faceDetected: false, blendshapes: new Map(), transformMatrix: null };
    }

    let result: FaceLandmarkerResult;
    try {
      result = this.landmarker.detectForVideo(video, timestamp);
    } catch {
      // Video may not be ready yet — return no-face gracefully
      return { faceDetected: false, blendshapes: new Map(), transformMatrix: null };
    }

    if (!result.faceBlendshapes || result.faceBlendshapes.length === 0) {
      return { faceDetected: false, blendshapes: new Map(), transformMatrix: null };
    }

    const blendshapes = new Map<string, number>();
    for (const bs of result.faceBlendshapes[0].categories) {
      blendshapes.set(bs.categoryName, bs.score);
    }

    const transformMatrix =
      result.facialTransformationMatrixes &&
      result.facialTransformationMatrixes.length > 0
        ? (Array.from(result.facialTransformationMatrixes[0].data) as number[])
        : null;

    return { faceDetected: true, blendshapes, transformMatrix };
  }

  destroy(): void {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
    }
  }
}
